'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { currentInteractionMachine } from './machines/currentInteractionMachine'
import type { Interaction, InteractionMessage } from '../../interaction/model/types'

export type CurrentInteractionMachineApi = ReturnType<
  typeof useMachine<typeof currentInteractionMachine>
>

const CurrentInteractionMachineContext = createContext<CurrentInteractionMachineApi | null>(null)

export function CurrentInteractionMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(currentInteractionMachine)
  return (
    <CurrentInteractionMachineContext.Provider value={api}>
      {children}
    </CurrentInteractionMachineContext.Provider>
  )
}

export function useCurrentInteractionMachine() {
  const ctx = useContext(CurrentInteractionMachineContext)
  if (!ctx) {
    throw new Error(
      'useCurrentInteractionMachine must be used within CurrentInteractionMachineProvider'
    )
  }
  return ctx
}

export function useCurrentInteractionMachineOptional() {
  return useContext(CurrentInteractionMachineContext)
}

export function useCurrentInteractionActions() {
  const machineApi = useCurrentInteractionMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setInteraction: (interaction: Interaction | null) =>
      send({ type: 'SET_INTERACTION', interaction }),
    setMessages: (messages: InteractionMessage[]) => send({ type: 'SET_MESSAGES', messages }),
    addMessage: (message: InteractionMessage) => send({ type: 'ADD_MESSAGE', message }),
    updateMessage: (id: string, updates: Partial<InteractionMessage>) =>
      send({ type: 'UPDATE_MESSAGE', id, updates }),
    removeMessage: (id: string) => send({ type: 'REMOVE_MESSAGE', id }),
    sortMessages: () => send({ type: 'SORT_MESSAGES' }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
    clear: () => send({ type: 'CLEAR' }),
  }), [send])
}

export function useCurrentInteractionListStoreCompat() {
  const machineApi = useCurrentInteractionMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      interaction: ctx.interaction,
      messages: ctx.messages,
      isLoading: ctx.isLoading,
      error: ctx.error,
      setInteraction: (interaction: typeof ctx.interaction) =>
        send({ type: 'SET_INTERACTION', interaction }),
      setMessages: (messages: InteractionMessage[]) => send({ type: 'SET_MESSAGES', messages }),
      addMessage: (message: InteractionMessage) => send({ type: 'ADD_MESSAGE', message }),
      updateMessage: (id: string, updates: Partial<InteractionMessage>) =>
        send({ type: 'UPDATE_MESSAGE', id, updates }),
      removeMessage: (id: string) => send({ type: 'REMOVE_MESSAGE', id }),
      sortMessages: () => send({ type: 'SORT_MESSAGES' }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
      clear: () => send({ type: 'CLEAR' }),
      getMessageById: (id: string) => ctx.messages.find((m) => m.id === id),
      getLatestMessage: () =>
        ctx.messages.length > 0 ? ctx.messages[ctx.messages.length - 1] : undefined,
      getMessagesCount: () => ctx.messages.length,
    }
  }, [machineApi])
}
